import React from 'react';
import { StandardTableActions } from '../../components/ui';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { KnowledgeBaseArticle } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { Badge } from '../../components/ui';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { ThemeColors } from '../../contexts/ThemeContext';
import DOMPurify from 'dompurify';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'KnowledgeBase',
    description: 'Create and manage knowledge base articles',
    permissionModuleKey: 'knowledge_base',
    route: {
      list: '/knowledge-base',
      new: '/knowledge-base/new',
      detail: (id: number) => `/knowledge-base/${id}`,
      edit: (id: number) => `/knowledge-base/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/knowledge-base',
      detail: (id: number) => `/knowledge-base/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search articles by title, summary, or content...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['title', 'status', 'categoryId'],
    },
    {
      name: 'tags',
      label: 'Tags',
      icon: '🏷️',
      fields: ['tags'],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: ['createdAt', 'updatedAt', 'publishedAt'],
    },
    {
      name: 'visibility',
      label: 'Visibility',
      icon: '👁️',
      fields: ['isFeatured', 'isPinned'],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'title',
        label: 'Title',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: KnowledgeBaseArticle) => item.title,
        options: [],
      },
      {
        key: 'status',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: KnowledgeBaseArticle) => item.status,
        options: ['draft', 'published', 'archived'],
      },
      {
        key: 'categoryId',
        label: 'Category',
        placeholder: 'All Categories',
        type: 'select' as const,
        getValue: (item: KnowledgeBaseArticle) => item.categoryId?.toString(),
        options: [], // Will be populated dynamically
      },
      {
        key: 'tags',
        label: 'Tags',
        placeholder: 'All Tags',
        type: 'text' as const,
        getValue: (item: KnowledgeBaseArticle) => item.tags?.join(', ') || '',
        options: [],
      },
      {
        key: 'isFeatured',
        label: 'Featured',
        placeholder: 'All',
        type: 'select' as const,
        getValue: (item: KnowledgeBaseArticle) => item.isFeatured ? 'Yes' : 'No',
        options: ['Yes', 'No'],
      },
      {
        key: 'isPinned',
        label: 'Pinned',
        placeholder: 'All',
        type: 'select' as const,
        getValue: (item: KnowledgeBaseArticle) => item.isPinned ? 'Yes' : 'No',
        options: ['Yes', 'No'],
      },
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: KnowledgeBaseArticle) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: [],
      },
      {
        key: 'updatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: KnowledgeBaseArticle) =>
          item.updatedAt
            ? formatDateGlobal(item.updatedAt)
            : undefined,
        options: [],
      },
      {
        key: 'publishedAt',
        label: 'Published At',
        placeholder: 'All Published At',
        type: 'date' as const,
        getValue: (item: KnowledgeBaseArticle) =>
          item.publishedAt
            ? formatDateGlobal(item.publishedAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic function
    applyFilter: (
      items: KnowledgeBaseArticle[],
      filters: {
        title?: string[] | string;
        status?: string[];
        categoryId?: string[];
        tags?: string[] | string;
        isFeatured?: string[];
        isPinned?: string[];
        createdAt?: string[];
        updatedAt?: string[];
        publishedAt?: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Title filter
        const titleFilter = filters.title;
        if (typeof titleFilter === 'string' && titleFilter.trim() !== '') {
          const value = (item.title || '').toLowerCase();
          if (!value.includes(titleFilter.toLowerCase())) return false;
        } else if (Array.isArray(titleFilter) && titleFilter.length > 0) {
          const value = (item.title || '').toLowerCase();
          const matches = titleFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.status && filters.status.length > 0) {
          if (!filters.status.includes(item.status)) return false;
        }

        // Category filter
        if (filters.categoryId && filters.categoryId.length > 0) {
          const categoryIdStr = item.categoryId?.toString();
          if (!categoryIdStr || !filters.categoryId.includes(categoryIdStr)) return false;
        }

        // Tags filter
        const tagsFilter = filters.tags;
        if (typeof tagsFilter === 'string' && tagsFilter.trim() !== '') {
          const itemTags = item.tags?.join(', ').toLowerCase() || '';
          if (!itemTags.includes(tagsFilter.toLowerCase())) return false;
        } else if (Array.isArray(tagsFilter) && tagsFilter.length > 0) {
          const itemTags = item.tags?.join(', ').toLowerCase() || '';
          const matches = tagsFilter.some((filter) => {
            return itemTags.includes(filter.toLowerCase());
          });
          if (!matches) return false;
        }

        // Featured filter
        if (filters.isFeatured && filters.isFeatured.length > 0) {
          const isFeaturedStr = item.isFeatured ? 'Yes' : 'No';
          if (!filters.isFeatured.includes(isFeaturedStr)) return false;
        }

        // Pinned filter
        if (filters.isPinned && filters.isPinned.length > 0) {
          const isPinnedStr = item.isPinned ? 'Yes' : 'No';
          if (!filters.isPinned.includes(isPinnedStr)) return false;
        }

        // Date filters
        const dateFields = ['createdAt', 'updatedAt', 'publishedAt'] as const;
        for (const field of dateFields) {
          const filterKey = field as keyof typeof filters;
          if (filters[filterKey] && (filters[filterKey] as string[]).length > 0) {
            if (ranges[field]) {
              const range = ranges[field];
              const itemDate = item[field] ? new Date(item[field] as string) : null;
              if (itemDate && range.start && range.end) {
                if (itemDate < range.start || itemDate > range.end) return false;
              }
            } else {
              const value = item[field]
                ? formatDateGlobal(item[field] as string)
                : '';
              const matches = (filters[filterKey] as string[]).some((filter) => {
                return value.includes(filter) || filter.includes(value);
              });
              if (!matches) return false;
            }
          }
        }

        return true;
      });
    },
  },

  // Export Configuration
  export: {
    enabled: true,
    formats: ['csv', 'excel', 'pdf'],
  },

  // Detail Configuration
  detail: {
    subtitle: 'Knowledge Base Article',
    notFoundTitle: 'Article not found',
    notFoundMessage: 'The article you are looking for does not exist or has been deleted.',
    notFoundBackText: 'Back to Knowledge Base',
    defaultTitle: 'Article Details',
    getTitle: (item: KnowledgeBaseArticle) => item.title,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter article title',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'slug',
      label: 'Slug',
      type: 'text',
      placeholder: 'Auto-generated from title',
      description: 'URL-friendly identifier (auto-generated if not provided)',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'summary',
      label: 'Summary',
      type: 'textarea',
      placeholder: 'Enter article summary',
      rows: 3,
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'content',
      label: 'Content',
      type: 'slate',
      required: true,
      placeholder: 'Enter article content',
      description: 'Notion-style block editor: headings, lists, quotes, links, images, and tables.',
      validation: {
        required: true,
      },
      contentKey: (formData: Record<string, any>) => String(formData?.id ?? 'new'),
    },
    {
      name: 'categoryId',
      label: 'Category',
      type: 'select',
      required: true,
      placeholder: 'Select a category',
      options: [], // Will be populated dynamically
      validation: {
        required: true,
      },
    },
    {
      name: 'tags',
      label: 'Tags',
      type: 'tags',
      placeholder: 'Add tags',
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      placeholder: 'Select status',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'archived', label: 'Archived' },
      ],
      defaultValue: 'draft',
    },
    {
      name: 'isFeatured',
      label: 'Featured',
      type: 'switch',
      switchLabel: 'Mark as featured article',
      description: 'Featured articles appear prominently in the list',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'isPinned',
      label: 'Pinned',
      type: 'switch',
      switchLabel: 'Pin to top',
      description: 'Pinned articles appear at the top of the list',
      labelPosition: 'left',
      defaultValue: false,
    },
  ] as FieldConfig[],

  // Toolbar Actions Configuration
  toolbar: {
    customButton: {
      visible: false,
      icon: '⚙️',
      title: 'Custom Action',
    },
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Article',
        showTitle: false,
        variant: 'primary' as const,
        href: '/knowledge-base/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: KnowledgeBaseArticle) => {
      window.location.href = `/knowledge-base/${item.id}`;
    },
    keyExtractor: (item: KnowledgeBaseArticle) => item.id,
  },

  // Empty States
  emptyStates: {
    noData: 'No articles found. Create your first article to get started.',
    noSearchResults: 'No articles match your search criteria.',
    noFilterResults: 'No articles match your filter criteria.',
  },

  // Delete Configuration
  delete: {
    title: 'Delete Article',
    message: 'Are you sure you want to delete this article? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete article',
    handler: async (id: number) => {
      const { deleteKnowledgeBaseArticle } = await import('./useData');
      await deleteKnowledgeBaseArticle(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Article',
      subtitle: 'Create a new knowledge base article',
      submitText: 'Create Article',
    },
    edit: {
      title: 'Edit Article',
      submitText: 'Update Article',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading article...',
    errorMessage: 'Failed to save article. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<KnowledgeBaseArticle>, files?: File[], _filesToDetach?: number[]) => {
      const { createKnowledgeBaseArticle } = await import('./useData');
      return await createKnowledgeBaseArticle(data, files);
    },
    update: async (id: number, data: Partial<KnowledgeBaseArticle>, files?: File[], filesToDetach?: number[]) => {
      const { updateKnowledgeBaseArticle } = await import('./useData');
      return await updateKnowledgeBaseArticle(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/knowledge-base/${entityId}/files/${fileId}`,
  },
};

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string, 
  categories?: any[],
  colors?: ThemeColors
): Column<KnowledgeBaseArticle>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const textSecondary = colors?.textSecondary || 'text-gray-600';
  const textMuted = colors?.textMuted || 'text-gray-400';
  
  return [
  {
    key: 'title',
    header: 'Title',
    sortable: true,
    render: (value, row) => (
      <Link
        to={moduleConfig.module.route.detail(row.id)}
        className={colors ? `${colors.primaryFg} font-medium` : 'text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium'}
      >
        <TruncatedText
          text={value || ''}
          maxLength={100}
          className="text-sm"
          showTooltip={true}
        />
      </Link>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    sortable: false,
    render: (value, row) => {
      if (row.category) {
        return (
          <div className="flex items-center gap-2">
            {row.category.icon && <span>{row.category.icon}</span>}
            <span
              className="px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: row.category.color ? `${row.category.color}20` : '#f3f4f6',
                color: row.category.color || '#374151',
              }}
            >
              {row.category.name}
            </span>
          </div>
        );
      }
      return <span className="text-gray-400 dark:text-gray-500">-</span>;
    },
  },
  {
    key: 'tags',
    header: 'Tags',
    sortable: false,
    render: (value, row) => {
      if (row.tags && row.tags.length > 0) {
        return (
          <div className="flex flex-wrap gap-1">
            {row.tags.slice(0, 3).map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {row.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{row.tags.length - 3}
              </Badge>
            )}
          </div>
        );
      }
      return <span className="text-gray-400 dark:text-gray-500">-</span>;
    },
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (value) => {
      const statusColors: Record<string, string> = {
        draft: 'secondary',
        published: 'accent',
        archived: 'secondary',
      };
      const statusLabels: Record<string, string> = {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived',
      };
      return (
        <Badge variant={statusColors[value as string] as any || 'secondary'}>
          {statusLabels[value as string] || value}
        </Badge>
      );
    },
  },
  {
    key: 'viewCount',
    header: 'Views',
    sortable: true,
    render: (value) => <span className={`text-sm ${textSecondary}`}>{value || 0}</span>,
  },
  {
    key: 'createdBy',
    header: 'Author',
    sortable: false,
    render: (value, row) => {
      if (row.createdBy) {
        return (
          <span className={`text-sm ${textSecondary}`}>
            {row.createdBy.userDisplayName ||
              `${row.createdBy.userFirstName || ''} ${row.createdBy.userLastName || ''}`.trim() ||
              'Unknown'}
          </span>
        );
      }
      return <span className={textMuted}>-</span>;
    },
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'updatedAt',
    header: 'Updated',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'attachments',
    header: 'Attachments',
    sortable: false,
    render: (_value, row) => {
      const count = row.attachments?.length ?? row._count?.attachments ?? 0;
      return count > 0 ? `${count} file${count !== 1 ? 's' : ''}` : '-';
    },
  },
  ];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case '📁':
    case 'folder':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to render table action buttons
export const getTableActions = (
  item: KnowledgeBaseArticle,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getDetailSections = (
  item: KnowledgeBaseArticle,
  formatDate: (dateString?: string) => string,
  _formatCurrency?: (amount?: number | null) => string,
  _mutate?: () => void,
  colors?: ThemeColors
) => {
  const textColor = colors?.text || 'text-gray-900';
  const sanitizedContent = DOMPurify.sanitize(item.content || '');

  return [
    {
      title: 'Article Content',
      icon: '📄',
      fields: [
        {
          label: '',
          value: (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          ),
        },
      ],
    },
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Title',
          value: item.title,
        },
        {
          label: 'Slug',
          value: item.slug,
        },
        {
          label: 'Summary',
          value: item.summary ? (
            <TruncatedText
              text={item.summary}
              maxLength={100}
              className="text-sm text-gray-900 dark:text-gray-100"
              showTooltip={true}
            />
          ) : 'No summary',
        },
        {
          label: 'Status',
          value: (
            <Badge
              variant={
                item.status === 'published'
                  ? 'accent'
                  : item.status === 'archived'
                  ? 'secondary'
                  : 'secondary'
              }
            >
              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </Badge>
          ),
        },
        {
          label: 'Category',
          value: item.category ? (
            <div className="flex items-center gap-2">
              {item.category.icon && <span>{item.category.icon}</span>}
              <span>{item.category.name}</span>
            </div>
          ) : (
            'No category'
          ),
        },
        {
          label: 'Tags',
          value:
            item.tags && item.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {item.tags.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : (
              'No tags'
            ),
        },
        {
          label: 'Featured',
          value: item.isFeatured ? 'Yes' : 'No',
        },
        {
          label: 'Pinned',
          value: item.isPinned ? 'Yes' : 'No',
        },
        {
          label: 'Views',
          value: item.viewCount || 0,
        },
        {
          label: 'Attachments',
          value: (() => {
            const count = item.attachments?.length ?? 0;
            return count > 0 ? `${count} file${count !== 1 ? 's' : ''}` : 'No files';
          })(),
        },
      ],
    },
    {
      title: 'Metadata',
      icon: 'ℹ️',
      fields: [
        {
          label: 'Created',
          value: formatDate(item.createdAt),
        },
        {
          label: 'Created By',
          value:
            item.createdBy?.userDisplayName ||
            `${item.createdBy?.userFirstName || ''} ${item.createdBy?.userLastName || ''}`.trim() ||
            'Unknown',
        },
        {
          label: 'Updated',
          value: formatDate(item.updatedAt),
        },
        {
          label: 'Updated By',
          value:
            item.updatedBy?.userDisplayName ||
            `${item.updatedBy?.userFirstName || ''} ${item.updatedBy?.userLastName || ''}`.trim() ||
            'Unknown',
        },
        {
          label: 'Published',
          value: item.publishedAt ? formatDate(item.publishedAt) : 'Not published',
        },
        {
          label: 'Version',
          value: item.version || 1,
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `${moduleConfig.module.api.detail(item.id)}/files`,
            files: item.attachments || [],
            defaultDownloadName: `article_${item.id}_files.zip`,
            getViewFileUrl: (fileId) => `${moduleConfig.module.api.detail(item.id)}/files/${fileId}`,
          }),
        },
      ],
    },
  ];
};

// Form fields
export const formFields: FieldConfig[] = moduleConfig.fields;

// Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
export const formLayout: FormLayout = {
  mode: 'default',
  sections: [
    {
      id: 'basic',
      title: 'Basic Information',
      icon: '📋',
      gridCols: 'md:grid-cols-12',
      fields: ['title', 'slug', 'summary'],
      fieldSpans: {
        title: 'md:col-span-6',
        slug: 'md:col-span-6',
        summary: 'md:col-span-12',
      },
      collapsible: true,
    },
    {
      id: 'content',
      title: 'Content',
      icon: '📄',
      gridCols: 'md:grid-cols-12',
      fields: ['content'],
      fieldSpans: { content: 'md:col-span-12' },
      collapsible: false,
    },
    {
      id: 'categorization',
      title: 'Categorization',
      icon: '🏷️',
      gridCols: 'md:grid-cols-2',
      fields: ['categoryId', 'tags'],
      collapsible: true,
    },
    {
      id: 'publishing',
      title: 'Publishing Options',
      icon: '📢',
      gridCols: 'md:grid-cols-2',
      fields: ['status', 'isFeatured', 'isPinned'],
      collapsible: true,
    },
  ],
};

// Initial form data
export const getInitialFormData = (): Partial<KnowledgeBaseArticle> => ({
  title: '',
  slug: '',
  summary: '',
  content: '',
  categoryId: undefined,
  tags: [],
  status: 'draft',
  isFeatured: false,
  isPinned: false,
});

// Map item to form data
export const mapItemToFormData = (item: KnowledgeBaseArticle): Partial<KnowledgeBaseArticle> => ({
  title: item.title,
  slug: item.slug,
  summary: item.summary || '',
  content: item.content,
  categoryId: item.categoryId,
  tags: item.tags || [],
  status: item.status,
  isFeatured: item.isFeatured,
  isPinned: item.isPinned,
});

// Status badges for detail page
export const getStatusBadges = (item: KnowledgeBaseArticle) => {
  const statusColors: Record<string, string> = {
    draft: 'secondary',
    published: 'accent',
    archived: 'secondary',
  };
  return (
    <Badge variant={statusColors[item.status] as any || 'secondary'}>
      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
    </Badge>
  );
};
